import { ReactNode } from 'react';
import { Body1Strong, Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  CheckmarkCircleFilled,
  CopyRegular,
  DocumentAddRegular,
  DocumentBulletListRegular,
  LinkRegular,
} from '@fluentui/react-icons';

import { CREATION_METHOD, TCreationMethod } from '../../models';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 720px)': { gridTemplateColumns: '1fr' },
  },
  card: {
    position: 'relative',
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selected: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  icon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  description: { color: tokens.colorNeutralForeground3 },
  check: {
    position: 'absolute',
    top: tokens.spacingVerticalS,
    right: tokens.spacingHorizontalS,
    color: tokens.colorBrandForeground1,
    fontSize: '20px',
  },
});

const METHODS: { value: TCreationMethod; description: string; icon: ReactNode }[] = [
  { value: CREATION_METHOD.BLANK, description: 'Start from an empty change.', icon: <DocumentAddRegular /> },
  {
    value: CREATION_METHOD.TEMPLATE,
    description: 'Start from a configured template with suggested sections, roles and tasks.',
    icon: <DocumentBulletListRegular />,
  },
  {
    value: CREATION_METHOD.PREVIOUS,
    description: 'Reuse the structure of a past change. Assessments and approvals are never copied.',
    icon: <CopyRegular />,
  },
  {
    value: CREATION_METHOD.SOURCE,
    description: 'Start from a complaint, audit finding, CAPA or other source record.',
    icon: <LinkRegular />,
  },
];

type TProps = {
  value?: TCreationMethod;
  onSelect: (method: TCreationMethod) => void;
};

export const CreationMethodCards = ({ value, onSelect }: TProps) => {
  const classes = useStyles();
  return (
    <div className={classes.grid}>
      {METHODS.map((method) => (
        <button
          key={method.value}
          type="button"
          className={mergeClasses(classes.card, value === method.value && classes.selected)}
          onClick={() => onSelect(method.value)}
          aria-pressed={value === method.value}
        >
          {value === method.value ? <CheckmarkCircleFilled className={classes.check} /> : null}
          <span className={classes.icon}>{method.icon}</span>
          <span className={classes.body}>
            <Body1Strong>{method.value}</Body1Strong>
            <Caption1 className={classes.description}>{method.description}</Caption1>
          </span>
        </button>
      ))}
    </div>
  );
};
